import { useState, useEffect } from 'react';
import { AlertCircle, TrendingDown, MessageCircle, Calendar, RefreshCw } from 'lucide-react';
import { api } from '../services/api';

interface KnowledgeGap {
  question: string;
  avg_confidence: number;
  occurrence_count: number;
  last_asked: string;
}

export default function KnowledgeGaps() {
  const [gaps, setGaps] = useState<KnowledgeGap[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGaps();
  }, []);

  const loadGaps = async () => {
    setLoading(true);
    try {
      const data = await api.getKnowledgeGaps(20);
      setGaps(data);
    } catch (error) {
      console.error('Failed to load knowledge gaps:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-50';
    if (score >= 0.4) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  if (gaps.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Knowledge Gaps Found</h3>
        <p className="text-gray-600 mb-6">
          Great! Your AI is performing well. No low-confidence questions in the last 30 days.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Knowledge Gaps</h2>
          <p className="text-gray-600">
            Questions your AI struggled to answer with high confidence. Add content for these topics to improve responses.
          </p>
        </div>
        <button
          onClick={loadGaps}
          className="flex items-center gap-2 px-4 py-2 text-black hover:bg-gray-50 rounded-lg transition"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900 mb-1">What are Knowledge Gaps?</h4>
            <p className="text-sm text-amber-800">
              These are questions from actual customer conversations where the AI's confidence was low (below 70%).
              Adding knowledge base articles for these topics will improve your AI's ability to help customers.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {gaps.map((gap, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-lg p-5 hover:border-purple-300 hover:shadow-md transition"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 mb-2 text-lg">
                  {gap.question}
                </h3>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(gap.avg_confidence)}`}>
                {Math.round(gap.avg_confidence * 100)}% confidence
              </span>
            </div>

            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                <span>Asked {gap.occurrence_count} time{gap.occurrence_count > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Last asked {formatDate(gap.last_asked)}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600 mb-2">
                <TrendingDown className="w-4 h-4 inline mr-1 text-orange-500" />
                <strong>Impact:</strong> This question appears frequently but your AI is unsure how to answer it.
              </p>
              <p className="text-sm text-black font-medium">
                → Add a knowledge base article about this topic to improve AI responses
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
